/*
 * fn: getAdIncontentIndexes
 *
 * Computes indexes for ads in a grid of a given array of groups containing recs. Follows rules:
 * - Ads take up 1 cell if single-column, otherwise span 2 cells
 * - Spacing is driven by GALLERY_INCONTENT_GAPS: rows before the first ad, then empty rows between
 * - In 1-2 column grids, ads wrap to the row after their rec
 * - In 2+ column grids, ads never break a row and leave empty space
 * - In 3+ column grids, ads are never first in a row
 * - In 4+ column grids, ads stagger across the target row
 *
 */
import { GALLERY_INCONTENT_GAPS } from '@/defs/ad'

type Props = {
  columns: number
  enabled?: boolean
  groupLengths: number[]
}

// Nearest gap config at or below the column count, so one entry covers a range of columns
const gapsForColumns = (columns: number) => {
  for (let key = columns; key > 0; key--) {
    const gaps = GALLERY_INCONTENT_GAPS[key]
    if (gaps) return gaps
  }
  return
}

export const getAdIncontentIndexes = ({ columns, enabled, groupLengths }: Props) => {
  const gaps = gapsForColumns(columns)
  if (!enabled || !gaps) return groupLengths.map(() => [])

  const { rowsBetween, rowsFirst } = gaps

  // How many cells an ad occupies
  const adCells = columns > 1 ? 2 : 1

  // Cell index of the overall grid: recs, ads, and empty cells
  let cellIndex = 0

  // Column offset staggers ads throughout the target row, max is the max offset before reset
  const columnOffsetMax = Math.max(0, columns - adCells - 1)
  let columnOffset = columnOffsetMax > 0 ? 1 : 0

  // Cell index for next ad, a row earlier where the ad wraps so rowsFirst counts full rec rows
  let cellNextAdIndex = (columns > 2 ? rowsFirst : rowsFirst - 1) * columns

  // Map ad indexes per group
  return groupLengths.map((groupLength) => {
    // Rec indexes in the group marked for ad placement after
    const indexes: number[] = []

    // For each rec in current group...
    for (let i = 0; i < groupLength; i++) {
      // Ensure there's enough room for the ad after the rec
      const adColumn = (cellIndex + 1) % columns

      // Valid: 1 col: always, 2 col: on row start, 3+ col: not on row start or row end
      const isValid = columns === 1 || (columns === 2 && adColumn === 0) || (adColumn !== 0 && adColumn !== columns - 1)

      // Place ad after cell if valid and cellIndex has reached the next ad
      if (isValid && cellIndex >= cellNextAdIndex) {
        indexes.push(i)

        // Next ad targets the row start plus the configured empty rows
        cellNextAdIndex = cellIndex - (cellIndex % columns) + (rowsBetween + 1) * columns + columnOffset

        columnOffset++
        if (columnOffset > columnOffsetMax) columnOffset = 0
        cellIndex += adCells
      }

      cellIndex += 1
    }

    // Add remaining empty cells to cell count
    cellIndex = Math.ceil(cellIndex / columns) * columns

    return indexes
  })
}
